/**
 * GrantedLineRow: a free-item line granted by a promotion on a price quote
 * (spec 2026-09-23 quote-and-lock). Rendered read-only — no stepper, no edit,
 * no remove — because the server owns granted lines; the customer never adds
 * or edits them. Mirrors CartScreen's `isReward` line styling.
 *
 * Formats its own price from the `currency` prop (rather than `<Price>`,
 * which resolves currency from `useBranch()`) so it renders standalone in
 * tests and anywhere else outside a `BranchProvider`.
 */
import "@/i18n";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui";

export type GrantedLineRowProps = {
  name: string;
  variantName?: string | null;
  promoName: string;
  /** Line subtotal before the grant — shown struck through since the line is free. */
  subtotal: number;
  currency?: string;
};

export function GrantedLineRow({ name, variantName, promoName, subtotal, currency }: GrantedLineRowProps) {
  const { t, i18n } = useTranslation();
  const formatted = currency
    ? new Intl.NumberFormat(i18n.language || "en", {
        style: "currency",
        currency,
        minimumFractionDigits: 2,
      }).format(subtotal)
    : subtotal.toFixed(2);

  return (
    <Card
      padding={12}
      style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 12, alignItems: "center" }}
    >
      <div style={{ minWidth: 0 }}>
        <div className="display" style={{ fontSize: 14, color: "var(--ink-1)", lineHeight: 1.2 }}>
          {name}
        </div>
        {variantName && (
          <div style={{ fontSize: 12, color: "var(--ink-2)", marginTop: 2 }}>{variantName}</div>
        )}
        <div style={{ fontSize: 12, fontWeight: 600, color: "var(--primary-700)", marginTop: 4 }}>
          {t("v2.checkout.free_with", { promo: promoName, defaultValue: "Free with {{promo}}" })}
        </div>
      </div>
      <div
        className="num"
        style={{ fontSize: 13, fontWeight: 700, color: "var(--ink-3)", textDecoration: "line-through" }}
      >
        {formatted}
      </div>
    </Card>
  );
}
